import { TRPCError } from '@trpc/server'
import { TRPCClientError } from '@trpc/client'

import { getHTTPStatusCodeFromError } from '@trpc/server/http'
import { omit } from 'es-toolkit/compat'
import { unknownErrorToError } from '@w/utils'
import {
  type TelErrorData,
  SerializableError,
  type TelServerErrorData,
  type TelError,
  type TelClientErrorData,
} from '../tel-error-data.js'
import type { SpanData } from '../tel-types.js'

export function prepareTelError({
  error,
  span,
  extra,
}: { error: unknown; span: SpanData; extra?: { componentStack?: string } }): TelErrorData {
  if (error === undefined) throw new Error('Error is undefined')

  const result = span.origin === 'client' ? prepareClientError(error) : prepareServerError(error)

  if (span) {
    result.traceId = span.traceId
    result.spanId = span.id
    result.requestId = span.requestId

    result.spanName = span.name
    result.spanType = span.type || ''
  }

  if (extra?.componentStack) {
    if (result.origin === 'client') {
      result.componentStack = extra.componentStack
    }
  }

  return result
}

function prepareServerError(error: unknown): TelServerErrorData {
  const obj = unknownErrorToError(error)
  const result: TelServerErrorData = {
    name: obj.name,
    message: obj.message,
    stack: obj.stack,

    type: 'unknown',
    origin: 'server',
    status: 500,
    spanName: '',
    spanType: '',
  }

  try {
    if (obj instanceof SerializableError) {
      const data = obj.toJSON()
      result.validationErrors = data.validationErrors
      result.cause = data.errorData
      result.causeStatusCode = data.code
      result.type = data.type
    } else if (obj instanceof TRPCError) {
      const status = getHTTPStatusCodeFromError(obj)
      result.status = status
      if (status < 500) result.type = 'client'
    }
    if (obj.cause !== undefined) {
      if (obj.cause && typeof obj.cause === 'object' && 'serializable' in obj.cause) {
        const data = (obj.cause as unknown as TelError).toJSON()
        result.type = data.type
        if (data.code)
          // if (data.stack) result.stack = data.stack // probably bad idea
          result.causeStatusCode = data.code
      } else if (obj.cause && obj.cause instanceof Error) {
        result.type = 'internal'
        result.cause = prepareServerError(obj.cause)
      } else {
        result.cause = safeSerialize(obj.cause)
      }
    } else {
      const cause: Record<string, unknown> = {}
      // Add all enumerable properties that aren't already processed
      for (const key in obj) {
        if (
          Object.prototype.hasOwnProperty.call(obj, key) &&
          // biome-ignore lint/suspicious/noPrototypeBuiltins: <explanation>
          !result.hasOwnProperty(key)
        ) {
          try {
            cause[key] = safeSerialize((obj as unknown as Record<string, unknown>)[key])
          } catch (propError) {
            cause[key] =
              `[Error accessing property: ${propError instanceof Error ? propError.message : String(propError)}]`
          }
        }
      }
      result.cause = cause
    }
  } catch (errorProcessingError) {
    result.message = `Error processing Error object: ${errorProcessingError instanceof Error ? errorProcessingError.message : String(errorProcessingError)}`
    result.cause = {
      name: obj.name,
      message: obj.message,
    }
  }

  return result
}

function prepareClientError(error: unknown) {
  const obj = unknownErrorToError(error)

  const result: TelClientErrorData = {
    name: obj.name,
    message: obj.message,
    stack: obj.stack,
    type: 'unknown',
    origin: 'client',
    spanName: '',
    spanType: '',
  }

  try {
    if (obj instanceof SerializableError) {
      const data = obj.toJSON()
      result.validationErrors = data.validationErrors
      result.cause = data.errorData
      result.type = data.type
    } else if (obj instanceof TRPCClientError) {
      result.type = 'server'
      result.cause = obj.data
      result.serverStatus = obj.data?.httpStatus || -1 // when illegal invocation is thrown the data will be undefined
    } else {
      if (obj.cause !== undefined) {
        if (obj.cause && typeof obj.cause === 'object' && 'serializable' in obj.cause) {
          const data = (obj.cause as unknown as TelError).toJSON()
          result.type = data.type
          result.cause = omit(data, 'type')
        } else if (obj.cause && obj.cause instanceof Error) {
          result.type = 'internal'
          result.cause = prepareClientError(obj.cause)
        } else {
          result.cause = safeSerialize(obj.cause)
        }
      } else {
        const cause: Record<string, unknown> = {}
        // Add all enumerable properties that aren't already processed
        for (const key in obj) {
          if (
            Object.prototype.hasOwnProperty.call(obj, key) &&
            // biome-ignore lint/suspicious/noPrototypeBuiltins: <explanation>
            !result.hasOwnProperty(key)
          ) {
            try {
              cause[key] = safeSerialize((obj as unknown as Record<string, unknown>)[key])
            } catch (propError) {
              cause[key] =
                `[Error accessing property: ${propError instanceof Error ? propError.message : String(propError)}]`
            }
          }
        }
        result.cause = cause
      }
    }
  } catch (errorProcessingError) {
    result.message = `Error processing Error object: ${errorProcessingError instanceof Error ? errorProcessingError.message : String(errorProcessingError)}`
    result.cause = {
      name: obj.name,
      message: obj.message,
    }
  }

  return result
}

/**
 * Safely serializes an object handling circular references
 */
function safeSerialize(value: unknown, seen = new WeakSet()): unknown {
  if (value === null || value === undefined) return value
  if (typeof value !== 'object') return value

  if (seen.has(value as object)) return '[Circular Reference]'
  seen.add(value as object)

  if (Array.isArray(value)) return value.map((item) => safeSerialize(item, seen))

  if (value instanceof Error)
    throw new Error('Error in safeSerialize not supported (is it even possible?)')

  // Handle regular objects
  const result: Record<string, unknown> = {}
  for (const [key, val] of Object.entries(value)) {
    try {
      result[key] = safeSerialize(val, seen)
    } catch (err) {
      result[key] =
        `[Error serializing property: ${err instanceof Error ? err.message : String(err)}]`
    }
  }

  return result
}
